<template>
  <Teleport to="body">
    <Transition name="wizard-fade" mode="out-in">
      <div v-if="currentStep === 'board'" key="board" class="wizard-toast" @click="advance">
        <div class="wizard-toast-icon wizard-toast-icon-board">
          <GamIcon :name="GamIconName.GAMBIT" size="16px" />
        </div>
        <p class="wizard-toast-text">{{ typedText }}</p>
        <button class="wizard-toast-close" @click.stop="advance">
          <GamIcon :name="GamIconName.X_CLOSE" size="12px" is-current />
        </button>
      </div>

      <div v-else-if="currentStep === 'puzzle'" key="puzzle" class="wizard-toast" @click="advance">
        <span class="wizard-toast-icon wizard-toast-icon-emoji">{{ userEmoji }}</span>
        <p class="wizard-toast-text">{{ typedText }}</p>
        <button class="wizard-toast-close" @click.stop="advance">
          <GamIcon :name="GamIconName.X_CLOSE" size="12px" is-current />
        </button>
      </div>

      <div v-else-if="currentStep === 'spark'" key="spark" class="wizard-toast" @click="advance">
        <div class="wizard-toast-icon wizard-toast-icon-spark">
          <GamIcon :name="GamIconName.PLAY_EMPTY" size="16px" />
        </div>
        <p class="wizard-toast-text">{{ typedText }}</p>
        <button class="wizard-toast-close" @click.stop="advance">
          <GamIcon :name="GamIconName.X_CLOSE" size="12px" is-current />
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import { currentMarkers } from '@/stores/marker.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

type WizardStep = 'board' | 'puzzle' | 'spark';

const route = useRoute();
const authStore = useAuthStore();
const userEmoji = computed(() => authStore.userDetails?.emoji || '👾');

const emit = defineEmits<(e: 'done' | 'requestLocation') => void>();

const STORAGE_KEY = 'gambits_map_wizard_seen';
const MARKER_WAIT_MS = 3000;
const TYPE_SPEED_MS = 25;
const PAUSE_AFTER_PUNCTUATION_MS = 120;

const steps: WizardStep[] = [];
const currentStep = ref<WizardStep | null>(null);
const typedText = ref('');
let stepIndex = 0;
let typeTimer: ReturnType<typeof setTimeout> | null = null;

const stopTyping = () => {
  if (typeTimer !== null) {
    clearTimeout(typeTimer);
    typeTimer = null;
  }
};

const startTyping = (fullText: string) => {
  stopTyping();
  typedText.value = '';
  let i = 0;

  const typeNext = () => {
    if (i < fullText.length) {
      typedText.value = fullText.slice(0, ++i);
      const char = fullText[i - 1];
      const delay = char === '.' || char === '!' || char === '\n' ? PAUSE_AFTER_PUNCTUATION_MS : TYPE_SPEED_MS;
      typeTimer = setTimeout(typeNext, delay);
    }
  };

  typeTimer = setTimeout(typeNext, TYPE_SPEED_MS);
};

const stepTextKeys: Record<WizardStep, string> = {
  board: 'map.wizard.board',
  puzzle: 'map.wizard.puzzle',
  spark: 'map.wizard.spark',
};

watch(currentStep, (step) => {
  if (step) {
    startTyping(localize(stepTextKeys[step]));
  } else {
    stopTyping();
    typedText.value = '';
  }
});

onBeforeUnmount(() => {
  stopTyping();
});

const advance = () => {
  const leavingStep = currentStep.value;
  stepIndex++;
  if (stepIndex < steps.length) {
    currentStep.value = steps[stepIndex];
  } else {
    currentStep.value = null;
    localStorage.setItem(STORAGE_KEY, 'true');
    emit('done');
  }

  if (leavingStep === 'spark') {
    emit('requestLocation');
  }
};

const startWizard = (hasBoardMarker: boolean) => {
  if (hasBoardMarker) {
    steps.push('board');
  }
  steps.push('puzzle', 'spark');

  stepIndex = 0;
  currentStep.value = steps[0];
};

onMounted(() => {
  if (localStorage.getItem(STORAGE_KEY) || route.query.spot) {
    emit('done');
    return;
  }

  if (currentMarkers.length > 0) {
    startWizard(true);
    return;
  }

  setTimeout(() => {
    startWizard(currentMarkers.length > 0);
  }, MARKER_WAIT_MS);
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;

.wizard-toast {
  position: fixed;
  z-index: 9999;
  bottom: calc(1.5rem + 3.5rem + 1rem);
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 2rem);
  max-width: 340px;
  padding: 1rem 2.5rem 1rem 1rem;
  background: var(--color-pink-20);
  backdrop-filter: blur(16px);
  border: 1px solid var(--color-dark-900);
  border-radius: 1rem;
  color: var(--color-white-80);
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
}

.wizard-toast-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.01em;
  white-space: pre-line;
  flex: 1;
}

.wizard-toast-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  color: var(--color-white-50);
  cursor: pointer;
  padding: 0;

  @include hover {
    color: var(--color-white);
  }

  @include press;
}

.wizard-toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.wizard-toast-icon-board {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: var(--color-linear-gradient);
  color: var(--color-white);
  box-shadow:
    0 2px 8px 0 var(--color-pink-30),
    0 0 16px 2px var(--color-pink-20);
}

.wizard-toast-icon-emoji {
  font-size: 22px;
  line-height: 1;
}

.wizard-toast-icon-spark {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--color-linear-gradient);
  color: var(--color-white);
  box-shadow:
    0 2px 8px 0 var(--color-pink-30),
    0 0 16px 2px var(--color-pink-20);
}

.wizard-fade-enter-active,
.wizard-fade-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.wizard-fade-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(1rem);
}

.wizard-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(1rem);
}
</style>
